import {
  completeArtworkRequestMutation,
  getArtworkRequestQueryKey,
  listArtworkRequestsInfiniteQueryKey,
  listArtworkRequestsQueryKey,
  updateArtworkStatusMutation,
  uploadArtworkRequestImageMutation,
} from "@hops/sdk/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useAuthStore } from "../../auth/auth-store";
import type { IAdobeHost } from "../../host/types";
import { useTimerStore } from "../../timer/timer-store";

interface UsePublishOptions {
  host: IAdobeHost;
}

interface PublishResult {
  previewPath: string;
  nativePath: string;
}

export function usePublish({ host }: UsePublishOptions) {
  const queryClient = useQueryClient();
  const [isExporting, setIsExporting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [exportResult, setExportResult] = useState<PublishResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const uploadMutation = useMutation(uploadArtworkRequestImageMutation());
  const completeMutation = useMutation(completeArtworkRequestMutation());
  const statusMutation = useMutation(updateArtworkStatusMutation());

  /**
   * Step 1: Export the current document as PNG preview + native file.
   * Call this before showing the publish dialog.
   */
  const exportArtwork = useCallback(
    async (requestId: string) => {
      setIsExporting(true);
      setError(null);
      setExportResult(null);

      try {
        if (!(await host.hasOpenDocument())) {
          throw new Error("No document is open");
        }

        const tempDir = await host.getTempDirectory();
        const previewPath = await host.exportAsPng(
          tempDir,
          `${requestId}_preview`,
        );
        const nativePath = await host.saveNativeFile(
          tempDir,
          `${requestId}_native`,
        );

        const result = { previewPath, nativePath };
        setExportResult(result);
        return result;
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Export failed";
        setError(msg);
        return null;
      } finally {
        setIsExporting(false);
      }
    },
    [host],
  );

  /**
   * Step 2: Upload exported files and complete the request.
   * Call after the user confirms in the publish dialog.
   */
  const publish = useCallback(
    async (
      requestInternalId: number,
      requestId: string,
      data: { totalMinutes: number; notes: string },
      exported?: PublishResult | null,
    ) => {
      setIsPublishing(true);
      setError(null);

      const files = exported ?? exportResult;
      if (!files) {
        setError("No exported files — export first");
        setIsPublishing(false);
        return false;
      }

      try {
        const user = useAuthStore.getState().user;

        // Upload preview PNG
        const previewBuffer = await host.readFileAsArrayBuffer(
          files.previewPath,
        );
        const previewBlob = new Blob([previewBuffer], { type: "image/png" });
        const previewFile = new File(
          [previewBlob],
          `${requestId}_preview.png`,
          {
            type: "image/png",
          },
        );

        // Wrap File in object so formDataBodySerializer can iterate entries
        // (Object.entries on a bare File returns [] — no own enumerable props)
        await uploadMutation.mutateAsync({
          path: { id: requestInternalId },
          body: { file: previewFile } as unknown as File,
        });

        // Upload native file (PSD or AI)
        const nativeBuffer = await host.readFileAsArrayBuffer(files.nativePath);
        const ext = host.appName === "photoshop" ? "psd" : "ai";
        const mimeType =
          host.appName === "photoshop"
            ? "image/vnd.adobe.photoshop"
            : "application/postscript";
        const nativeBlob = new Blob([nativeBuffer], { type: mimeType });
        const nativeFile = new File(
          [nativeBlob],
          `${requestId}_native.${ext}`,
          {
            type: mimeType,
          },
        );

        await uploadMutation.mutateAsync({
          path: { id: requestInternalId },
          body: { file: nativeFile } as unknown as File,
        });

        // Complete the request
        await completeMutation.mutateAsync({
          path: { id: requestInternalId },
          body: {
            artPerson: user?.username ?? null,
            artChecker: null,
            artTotalMinutes: data.totalMinutes,
            artOnlyMinutes: data.totalMinutes,
            dieSizeToOrder: null,
            completionNotes: data.notes || null,
            attachmentUrl: null,
            attachmentThumb: null,
            sendEmail: true,
          },
        });

        // Try to move to RVEW (review) — non-fatal if the status code doesn't exist yet
        try {
          await statusMutation.mutateAsync({
            path: { id: requestInternalId },
            body: {
              statusCode: "RVEW",
              note:
                data.notes ||
                `Completed from ${host.appName === "photoshop" ? "Photoshop" : "Illustrator"} panel`,
              attachmentUrl: null,
              attachmentThumb: null,
              sendEmail: true,
            },
          });
        } catch {
          // RVEW status code may not be configured — completion still succeeded
          console.warn(
            "[usePublish] RVEW status update skipped (code may not exist)",
          );
        }

        // Stop timer
        useTimerStore.getState().stop();

        // Invalidate queries
        queryClient.invalidateQueries({
          queryKey: listArtworkRequestsQueryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: listArtworkRequestsInfiniteQueryKey(),
        });
        queryClient.invalidateQueries({
          queryKey: getArtworkRequestQueryKey({
            path: { id: requestInternalId },
          }),
        });

        setExportResult(null);
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Publish failed");
        return false;
      } finally {
        setIsPublishing(false);
      }
    },
    [
      host,
      exportResult,
      uploadMutation,
      completeMutation,
      statusMutation,
      queryClient,
    ],
  );

  return {
    exportArtwork,
    publish,
    isExporting,
    isPublishing,
    exportResult,
    error,
  };
}
